export type UOMType = 'reference' | 'bigger' | 'smaller';
export type UOMStatus = 'active' | 'archived';
export type ProductUOMRole = 'stock' | 'purchase' | 'sales';

export interface UOM {
  id: string;
  name: string;
  orgId?: string;
  category: string;
  type: UOMType;
  ratio: number;
  status: UOMStatus;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export type ProductUOMFields = Partial<Record<`${ProductUOMRole}UomId`, string>> &
  Partial<Record<`${ProductUOMRole}UomName`, string>> &
  Partial<Record<`${ProductUOMRole}UomCategory`, string>>;

const fieldName = (role: ProductUOMRole, suffix: 'Id' | 'Name' | 'Category') =>
  `${role}Uom${suffix}` as const;

export function getProductUomId(item: unknown, role: ProductUOMRole): string {
  if (!item || typeof item !== 'object') return '';
  const value = (item as Record<string, unknown>)[fieldName(role, 'Id')];
  return typeof value === 'string' ? value : '';
}

export function withProductUom<T extends object>(item: T, role: ProductUOMRole, uom?: UOM): T & ProductUOMFields {
  return {
    ...item,
    [fieldName(role, 'Id')]: uom?.id || '',
    [fieldName(role, 'Name')]: uom?.name || '',
    [fieldName(role, 'Category')]: uom?.category || '',
  } as T & ProductUOMFields;
}

export function activeUoms(uoms: UOM[]): UOM[] {
  return uoms
    .filter(uom => uom.status === 'active')
    .sort((left, right) => left.category.localeCompare(right.category) || left.name.localeCompare(right.name));
}

export function uomsInCategory(uoms: UOM[], category?: string): UOM[] {
  const available = activeUoms(uoms);
  return category ? available.filter(uom => uom.category === category) : available;
}

export function compatibleUoms(uoms: UOM[], stockUomId?: string): UOM[] {
  if (!stockUomId) return activeUoms(uoms);
  const stockUom = uoms.find(uom => uom.id === stockUomId);
  return uomsInCategory(uoms, stockUom?.category);
}

export function resolveUom(uoms: UOM[], value?: string): UOM | undefined {
  if (!value) return undefined;
  return uoms.find(uom => uom.id === value || uom.name === value);
}

/** Converts a quantity between units in the same category via their reference-unit ratios. */
export function convertUomQuantity(quantity: number, from: UOM, to: UOM): number {
  if (from.category !== to.category) {
    throw new Error(`Cannot convert ${from.category} to ${to.category}`);
  }
  if (!Number.isFinite(quantity) || !Number.isFinite(from.ratio) || !Number.isFinite(to.ratio) || from.ratio <= 0 || to.ratio <= 0) {
    throw new Error('Quantity and UOM ratios must be finite, with ratios greater than zero');
  }
  return quantity * from.ratio / to.ratio;
}

export function movementMatchesItem(movement: unknown, itemId: string): boolean {
  if (!movement || typeof movement !== 'object') return false;
  const data = movement as Record<string, unknown>;
  return data.itemId === itemId || data.item_id === itemId || data.productId === itemId || data.product_id === itemId;
}
